import {
  defaultTranslate,
  getReferenceLabel,
  MachineRef,
  Translate,
} from "./machineReferenceLabels";
import { capitalizeFirst, composeEnglishLegacy, composeLocalized } from "./machineNameComposer";

/** A reference row as the machine view models carry it (MACH-REF-I18N-1: `code` is optional and
 *  absent until the Machinery API that sends it is live). */
type BaseRef = { name: string; code?: string | null };

export interface BaseMachine {
  model: string;
  manufacturer?: {
    name: string;
  };
  customBrandName?: string;
  productionYear?: number;
  type?: BaseRef;
  axisConfiguration?: BaseRef;
  operationType?: BaseRef;
  automationLevel?: BaseRef;
}

export const getMachineDisplayName = (machine: BaseMachine): string => {
  const brandName = machine?.customBrandName || machine?.manufacturer?.name || "";
  return brandName + " " + machine?.model;
};

export const getProductionYearDisplay = (machine: BaseMachine): string => {
  return machine?.productionYear && machine?.productionYear > 0 ? machine?.productionYear.toString() : "";
};

export const getBrandDisplayName = (machine: BaseMachine): string => {
  return machine?.customBrandName || machine?.manufacturer?.name || "";
};

/**
 * Formats manufacturer name with parent-child hierarchy using chevron breadcrumb.
 * e.g. "Tajmac-ZPS > Manurhin" when parent exists, just "DMG Mori" when no parent.
 */
export const formatManufacturerWithHierarchy = (
  name: string,
  parentName: string | null | undefined
): string => {
  if (!parentName) return name;
  return `${parentName} > ${name}`;
};

/**
 * Localised machine TYPE label (filters, chips, KV rows, group headers, dropdowns). Pass the row
 * (`machine.type`, `{ name: typeName, code: typeCode }`) so it resolves by code; a bare name still
 * works (name -> code for the closed type set), and an unknown row shows the server's own name.
 */
export const getMachineTypeLabel = (
  type: MachineRef | string | null | undefined,
  translate: Translate = defaultTranslate
): string => getReferenceLabel("type", typeof type === "string" ? { name: type } : type, translate);

/**
 * The machine designation ("5-Axis CNC Milling Machine" / "Fresatrice CNC a 5 assi"), composed from
 * per-language templates by reference code — see machineNameComposer.ts (spec §4.2). Any missing code
 * or key -> the whole name in English, never a mixed-language phrase.
 */
export const getMachineName = (
  machine?: BaseMachine | null,
  translate: Translate = defaultTranslate
): string => {
  if (!machine || !(machine.type?.name || machine.type?.code)) return "";
  return capitalizeFirst(composeLocalized(machine, translate) ?? composeEnglishLegacy(machine));
};
